import React, { useState, useMemo } from 'react';
import styles from './ObjectMassEditPanel.module.css';
import { CloseIcon, BoundaryIcon, BuildingIcon, RoadIcon, PinIcon, TrashIcon } from '../Icon';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

const ObjectMassEditPanel = ({ isVisible, onClose, onApply, onDelete, selectedFeatures, legendData }) => {
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [editData, setEditData] = useState({
        owner: '',
        description: '',
        for_sale: null, // null means no change, true/false means update
        is_building: null,
        is_planned: null,
        categories: {} // type-based categories: { plot: 'cat_id', building: 'cat_id', ... }
    });

    const selectedTypes = useMemo(() => {
        const types = new Set();
        selectedFeatures.forEach(f => {
            let type = f.properties.type;
            if (type === 'road') type = 'line'; // normalization if needed
            types.add(type);
        });
        return Array.from(types);
    }, [selectedFeatures]);

    const handleUpdateField = (field, value) => {
        setEditData(prev => ({ ...prev, [field]: value }));
    };

    const handleUpdateCategory = (type, categoryId) => {
        setEditData(prev => ({
            ...prev,
            categories: { ...prev.categories, [type]: categoryId }
        }));
    };

    const handleApply = () => {
        // Construct the update payload
        const updatedFeatures = selectedFeatures.map(f => {
            const newFeature = JSON.parse(JSON.stringify(f));
            const type = f.properties.type === 'road' ? 'line' : f.properties.type;

            if (editData.owner) newFeature.properties.owner = editData.owner;
            if (editData.description) newFeature.properties.description = editData.description;
            
            if (editData.for_sale !== null) newFeature.properties.for_sale = editData.for_sale;
            if (editData.is_building !== null && (type === 'building' || type === 'line')) {
                newFeature.properties.is_building = editData.is_building;
            }
            if (editData.is_planned !== null) newFeature.properties.is_planned = editData.is_planned;

            // Type-specific category
            if (editData.categories[type]) {
                newFeature.properties.category = editData.categories[type];
                
                // Also update color/hatch if they are tied to category in this system?
                // Usually, the frontend/backend handle this during render, but let's see.
                // In some systems, category change implies color change.
                const categories = type === 'plot' ? legendData.plots : 
                                 type === 'building' ? legendData.buildings :
                                 (type === 'line' || type === 'road') ? legendData.roads :
                                 type === 'marker' ? legendData.markers : [];
                
                const selectedCat = categories.find(c => c.id === editData.categories[type]);
                if (selectedCat) {
                    newFeature.properties.color = selectedCat.color;
                    if (selectedCat.hatch) newFeature.properties.hatch = selectedCat.hatch;
                    if (selectedCat.lineType) newFeature.properties.lineType = selectedCat.lineType;
                }
            }

            return newFeature;
        });

        onApply(updatedFeatures);
    };

    const handleDelete = () => {
        setIsDeleteConfirmOpen(true);
    };

    if (!isVisible) return null;

    const getTypeIcon = (type) => {
        switch (type) {
            case 'plot': return <BoundaryIcon size={16} />;
            case 'building': return <BuildingIcon size={16} />;
            case 'line': case 'road': return <RoadIcon size={16} />;
            case 'marker': return <PinIcon size={16} />;
            default: return null;
        }
    };

    const getTypeName = (type) => {
        switch (type) {
            case 'plot': return 'Участки';
            case 'building': return 'Здания';
            case 'line': case 'road': return 'Линии';
            case 'marker': return 'Метки';
            default: return type;
        }
    };

    const getTypeCategories = (type) => {
        if (!legendData) return [];
        switch (type) {
            case 'plot': return legendData.plots || [];
            case 'building': return legendData.buildings || [];
            case 'line': case 'road': return legendData.roads || [];
            case 'marker': return legendData.markers || [];
            default: return [];
        }
    };

    return (
        <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.header}>
                <h3>Массовое редактирование</h3>
                <button onClick={onClose} className={styles.closeButton}><CloseIcon size={20} /></button>
            </div>
            <div className={styles.content}>
                <div className={styles.selectedInfo}>
                    Выбрано объектов: {selectedFeatures.length}
                </div>

                <div className={styles.section}>
                    <h4>Общие данные</h4>
                    <div className={styles.inputGroup}>
                        <label className={styles.label}>Владелец</label>
                        <input 
                            type="text" 
                            className={styles.input} 
                            placeholder="Оставьте пустым, чтобы не менять"
                            value={editData.owner}
                            onChange={(e) => handleUpdateField('owner', e.target.value)}
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label className={styles.label}>Описание</label>
                        <textarea 
                            className={styles.textarea} 
                            placeholder="Оставьте пустым, чтобы не менять"
                            value={editData.description}
                            onChange={(e) => handleUpdateField('description', e.target.value)}
                        />
                    </div>
                </div>

                <div className={styles.section}>
                    <h4>Статусы</h4>
                    <div className={styles.statusGroup}>
                        <label className={styles.checkboxLabel}>
                            <input 
                                type="checkbox" 
                                checked={editData.for_sale === true}
                                ref={el => el && (el.indeterminate = editData.for_sale === null)}
                                onChange={(e) => handleUpdateField('for_sale', editData.for_sale === true ? false : (editData.for_sale === false ? null : true))}
                            />
                            В продаже
                        </label>
                        {selectedTypes.some(t => t === 'building' || t === 'line' || t === 'road') && (
                            <label className={styles.checkboxLabel}>
                                <input 
                                    type="checkbox" 
                                    checked={editData.is_building === true}
                                    ref={el => el && (el.indeterminate = editData.is_building === null)}
                                    onChange={(e) => handleUpdateField('is_building', editData.is_building === true ? false : (editData.is_building === false ? null : true))}
                                />
                                В строительстве
                            </label>
                        )}
                        <label className={styles.checkboxLabel}>
                            <input 
                                type="checkbox" 
                                checked={editData.is_planned === true}
                                ref={el => el && (el.indeterminate = editData.is_planned === null)}
                                onChange={(e) => handleUpdateField('is_planned', editData.is_planned === true ? false : (editData.is_planned === false ? null : true))}
                            />
                            Запланировано
                        </label>
                    </div>
                    <div className={styles.label} style={{ marginTop: '5px', fontSize: '10px' }}>
                        * Кликните несколько раз для выбора (Да / Нет / Не менять)
                    </div>
                </div>

                <div className={styles.section}>
                    <h4>Категории по типам</h4>
                    {selectedTypes.map(type => (
                        <div key={type} className={styles.typeSection}>
                            <div className={styles.typeTitle}>
                                {getTypeIcon(type)}
                                <span>{getTypeName(type)}</span>
                            </div>
                            <div className={styles.categorySelectContainer}>
                                <select 
                                    className={styles.select}
                                    value={editData.categories[type] || ''}
                                    onChange={(e) => handleUpdateCategory(type, e.target.value)}
                                >
                                    <option value="">Не менять категорию</option>
                                    {getTypeCategories(type).map(cat => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className={styles.footer}>
                <button 
                    className={styles.applyButton} 
                    onClick={handleApply}
                >
                    Применить изменения
                </button>
                <div className={styles.secondaryButtons}>
                    <button className={styles.cancelButton} onClick={onClose}>
                        Отмена
                    </button>
                    <button className={styles.deleteButton} onClick={handleDelete} title="Удалить выбранные объекты">
                        <TrashIcon size={16} />
                        Удалить
                    </button>
                </div>
            </div>

            <ConfirmDialog
                show={isDeleteConfirmOpen}
                onClose={() => setIsDeleteConfirmOpen(false)}
                onConfirm={() => {
                    setIsDeleteConfirmOpen(false);
                    onDelete(selectedFeatures.map(f => f.id));
                }}
                title="Удалить выбранные объекты?"
                message={
                    <>
                        Вы действительно хотите удалить <strong>{selectedFeatures.length} объектов</strong>? Это действие необратимо.
                    </>
                }
                confirmLabel="Удалить"
                cancelLabel="Отмена"
                destructive
            />
        </div>
    );
};

export default ObjectMassEditPanel;
